import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Modules' },
    { id: 'searching', name: 'Rental Hunting & Escrow' },
    { id: 'selling', name: 'Asset Listings (10k UGX)' },
    { id: 'payments', name: 'Payout Verification' },
    { id: 'security', name: 'Anti-Agent Fraud Protection' }
  ];

  const faqs = [
    {
      category: 'searching',
      question: 'How does the HireBroker secure escrow payout mechanism protect my funds?',
      answer: 'When you secure a rental unit, your cash commitment is deposited directly into our centralized framework. The local area broker is only compensated after you perform a physical inspection, confirm accuracy, and execute your move-in keys.'
    },
    {
      category: 'searching',
      question: 'What criteria determines if a property asset receives "Verified" infrastructure rank?',
      answer: 'Every property undergoes a 24-hour verification window. Our field operations team checks physical structural data coordinates, title integrity records, and active vacancy status to protect diaspora and local clients alike.'
    },
    {
      category: 'selling',
      question: 'What is included in the flat 10,000 UGX listing model?',
      answer: 'For a single non-recurring payment of 10,000 UGX, your rental asset or land plot is pushed instantly onto the internal dashboards of verified matching area brokers containing pre-qualified buyers. No percentage cuts.'
    },
    {
      category: 'payments',
      question: 'Which transaction methods are supported for escrow processing?',
      answer: 'We securely handle instantaneous clearings via integrated Mobile Money lines (MTN and Airtel), standard banking wire systems, and centralized digital payment paths.'
    },
    {
      category: 'security',
      question: 'How do you filter out fraudulent street brokers from the ecosystem?',
      answer: 'Casual agents are strictly prohibited. Every broker on our platform undergoes a baseline identity check, zone expertise audit, and operates under strict feedback rating protocols monitored directly by platform administration.'
    }
  ];

  const filteredFaqs = activeCategory === 'all'
    ? faqs
    : faqs.filter(faq => faq.category === activeCategory);

  const searchedFaqs = searchQuery
    ? filteredFaqs.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : filteredFaqs;

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Knowledge Base & Infrastructure
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6">
                System operations <span className="font-serif italic text-neutral-500 block sm:inline">documentation.</span>
              </h1>
              <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-xl">
                Demystify real estate escrow networks, explore listing configurations, and resolve your transaction parameters with absolute technical precision.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: INTERACTIVE MATRIX SEARCH (Soft Chalk Grey Background) */}
        <section className="bg-neutral-50 border-y border-neutral-200/60 py-24">
          <div className="max-w-4xl mx-auto px-6">
            
            {/* Minimal Search Field */}
            <div className="relative max-w-2xl mx-auto mb-12">
              <input
                type="search"
                placeholder="Search system parameters, escrow keywords, or asset terms..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white px-5 py-4 pl-12 text-base text-neutral-900 border border-neutral-200 rounded-none focus:outline-none focus:border-neutral-950 transition-colors shadow-sm"
              />
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Architecture Category Filtering Tabulation */}
            <div className="flex flex-wrap gap-2 mb-12 justify-center border-b border-neutral-200/40 pb-6">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-4 py-2 text-xs uppercase tracking-wider transition-all duration-150 rounded-none border ${
                    activeCategory === category.id
                      ? 'bg-neutral-950 border-neutral-950 text-white font-medium'
                      : 'bg-white border-neutral-200 text-neutral-500 hover:text-neutral-950 hover:border-neutral-300'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            {/* Architectural FAQ Accordion Structure */}
            <div className="space-y-4">
              {searchedFaqs.length > 0 ? (
                searchedFaqs.map((faq, index) => (
                  <details 
                    key={index} 
                    className="group bg-white border border-neutral-200/60 open:border-neutral-300 transition-all"
                  >
                    <summary className="list-none flex items-center justify-between px-6 py-4 cursor-pointer font-medium text-neutral-950 text-sm sm:text-base hover:bg-neutral-50 transition-colors">
                      <div className="flex gap-3">
                        <span className="text-red-600 font-mono text-sm pt-0.5 group-open:hidden">＋</span>
                        <span className="text-red-600 font-mono text-sm pt-0.5 hidden group-open:inline">－</span>
                        <span>{faq.question}</span>
                      </div>
                    </summary>
                    <div className="px-6 pb-6 pt-2 text-sm text-neutral-500 font-light leading-relaxed border-t border-neutral-100 pl-11">
                      {faq.answer}
                    </div>
                  </details>
                ))
              ) : (
                <div className="text-center py-12 bg-white border border-neutral-200/40">
                  <span className="text-xs uppercase tracking-widest text-neutral-400 font-mono block mb-2">
                    Zero Node Results
                  </span>
                  <p className="text-sm text-neutral-500 font-light">
                    Adjust query arguments or filter for alternative systemic operational sections above.
                  </p>
                </div>
              )}
            </div>

          </div>
        </section>


        {/* SECTION 3: CORE OPERATIONAL STATS (Pristine White Background) */}
        <section className="bg-white py-24 border-b border-neutral-100">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-3 gap-12 text-left">
              {[
                { title: 'Automated Shielding', description: 'Real-time property coordinates and title tracking vectors run autonomously on any platform submission.' },
                { title: 'Escrow Lockouts', description: 'Funds remain held securely until absolute physical verification signatures occur within the field.' },
                { title: 'Vetted Broker Network', description: 'Zero informal agent proxy layers. Route directly to evaluated municipal experts with active tenant bases.' }
              ].map((item, index) => (
                <div key={index} className="space-y-3 border-l-2 border-neutral-100 pl-6 focus-within:border-red-600 transition-colors">
                  <h3 className="text-base font-medium text-neutral-950 tracking-tight">{item.title}</h3>
                  <p className="text-sm text-neutral-500 font-light leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>


        {/* SECTION 4: DIRECT CONNECT SUPPORT BLOCK (Warm Muted Grey Background) */}
        <section className="bg-neutral-100/70 py-24">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="mb-12">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-2">
                Escalation Matrix
              </span>
              <h2 className="text-3xl font-light text-neutral-950 tracking-tight">Need granular transaction help?</h2>
              <p className="text-sm text-neutral-500 font-light mt-2 max-w-md mx-auto">
                If structural listing friction or transaction processing variations happen, reach an administrator instantly.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {[
                { label: 'Security & Payout Desk', value: 'support@hirebroker.co' },
                { label: 'Direct Operations Call', value: '+256 774 509 859' },
                { label: 'System Live Chat Link', value: 'Available 24/7 Monitored' }
              ].map((contact, index) => (
                <div key={index} className="bg-white border border-neutral-200/50 p-6 shadow-sm flex flex-col justify-center">
                  <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium block mb-1">
                    {contact.label}
                  </span>
                  <span className="text-sm font-medium text-neutral-950 break-all">
                    {contact.value}
                  </span>
                </div>
              ))}
            </div>

            <a 
              href="/contact"
              className="inline-flex items-center justify-center px-8 py-3.5 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-200"
            >
              Open Direct Administration Ticket
            </a>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default HelpCenter;